import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import classNames from 'classnames'
import { format, subDays } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import { IconPlayerStop, IconSparkles } from '@tabler/icons-react'

import { apiService } from '@/api/apiService'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Progress } from '@/components/progress/Progress'
import { PageHeader } from '@/components/headers/PageHeader'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { getOrganizationOptions } from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import {
  clearAllFilters,
  fetchCallCount,
  formatCallFilters,
} from '@/reducers/callSearch/callSearch.actions'
import {
  initialState as callSearchInitialState,
  setDateRangeFilters,
  setFilters,
  setTotalCount,
} from '@/reducers/callSearch/callSearch.redux'
import { CallSearchFilters } from '@/views/Calls/CallSearchFilters'
import { CHANNEL_FILTER } from '@/views/Calls/CallSearch.constants'
import { useAutomationInsights, ANALYSIS_STATES } from '@/views/AutomationInsights/helpers'

import { exportToPDF } from './AutomationInsightsExport'
import { AutomationInsightsReport, formatSummaryCards, getApiUrl } from './AutomationInsightsReport'

const MINIMUM_CALLS = 50
const SAMPLE_SIZE = 100

const callCountMessage = (counting, totalCount, hasKeywords) => {
  if (counting) {
    return 'Counting calls that match these filters...'
  }
  if (isNil(totalCount)) {
    return `Analyzes a random sample of calls - minimum ${MINIMUM_CALLS} required`
  }
  if (totalCount < MINIMUM_CALLS) {
    return `Only ${totalCount.toLocaleString()} calls match these filters - at least ${MINIMUM_CALLS} are needed. Widen the filters to run.`
  }
  // Call Explorer's count doesn't apply keyword filters, so with keywords it's an upper bound.
  const matched = hasKeywords
    ? `Up to ${totalCount.toLocaleString()} calls match these filters (keyword matches aren't counted)`
    : `${totalCount.toLocaleString()} calls match these filters`
  return `${matched}. Analyzes a random sample of up to ${SAMPLE_SIZE}.`
}
const AutomationInsightsHeader = () => {
  return (
    <PageHeader
      title="Automation Insights"
      decoration="lightning-bolt"
      data-testid="automation-insights-page"
    />
  )
}

// Call Explorer's own filter panel (as on Dynamic Insights), so a run takes every
// filter Call Explorer has. Voice calls only, so the channel filter is hidden.
const AutomationInsightsFilters = ({
  onRunAnalysis,
  onStopAnalysis,
  onApplyFilters,
  onClearFilters,
  carriedOrganizationId,
  isReadyToRun,
  isRunning,
  counting,
  totalCount,
  hasKeywords,
}) => {
  return (
    <>
      <CallSearchFilters
        params={{ organizationId: carriedOrganizationId }}
        hiddenFilters={[CHANNEL_FILTER]}
        allow120Days
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <Card className="mb">
        <div className="flex-align-center flex-space-between">
          <div className="flex-align-center small-gap">
            <span
              className={classNames('muted-text', { bold: isReadyToRun })}
              data-testid="automation-insights-call-count"
            >
              {callCountMessage(counting, totalCount, hasKeywords)}
            </span>
          </div>
          <div>
            {!isRunning && (
              <Button primary className="no-wrap" disabled={!isReadyToRun} onClick={onRunAnalysis}>
                <IconSparkles />
                Run Analysis
              </Button>
            )}
            {isRunning && (
              <Button className="no-wrap" onClick={onStopAnalysis}>
                <IconPlayerStop />
                Stop
              </Button>
            )}
          </div>
        </div>
      </Card>
    </>
  )
}

const AnalysisProgress = ({ progress, message }) => {
  return (
    <div className="animated-gradient-border mb">
      <Card title="Analyzing conversations">
        <div className="mb">
          <Progress value={progress} success={progress >= 100} />
        </div>
        <div>
          <div className="bold">{message || 'Processing...'}</div>
          <div className="muted-text">This usually takes a couple of minutes</div>
        </div>
      </Card>
    </div>
  )
}

export const AutomationInsightsPage = () => {
  const dispatch = useDispatch()
  const { username, organizationid, token } = useSelector((state) => state.currentUser)
  const { organizations } = useSelector((state) => state)
  const store = useStore()
  const { filters, data: callSearchData, totalCount } = useSelector((state) => state.callSearch)
  const { organizationId, startDate, endDate } = filters

  const apiUrl = getApiUrl(apiService)

  const {
    analysisState,
    analysisProgress,
    analysisMessage,
    analysisReport,
    analysisError,
    startAnalysis,
    stopAnalysis,
    resetAnalysis,
  } = useAutomationInsights({ apiUrl, username, organizationid, token })

  const [expandedSections, setExpandedSections] = useState({})
  const [isExporting, setIsExporting] = useState(false)
  const [showExportWarning, setShowExportWarning] = useState(false)
  const [carriedOrganizationId] = useState(filters.organizationId)
  const countRequestRef = useRef(0)
  const [counting, setCounting] = useState(false)

  // Precheck: Call Explorer's count for the applied filters (voice calls, like the run).
  const refreshCallCount = useCallback(async () => {
    const { filters: applied, data } = store.getState().callSearch
    if (!applied.organizationId || !applied.startDate || !applied.endDate) {
      return
    }
    const request = ++countRequestRef.current
    setCounting(true)
    dispatch(setTotalCount(null))
    await dispatch(
      fetchCallCount({
        filters: { ...applied, channel: CHANNEL_TYPE.CALL },
        playbookData: data.playbookData,
      })
    )
    if (request === countRequestRef.current) {
      setCounting(false)
    }
  }, [dispatch, store])

  useEffect(() => {
    if (isEmpty(organizations)) {
      dispatch(fetchOrganizations())
    }
  }, [dispatch, organizations])

  useEffect(() => {
    if (!isEmpty(analysisReport?.sections)) {
      setExpandedSections(
        analysisReport.sections.reduce((acc, section, index) => {
          acc[section.type] = index === 0
          return acc
        }, {})
      )
    }
  }, [analysisReport])

  useEffect(() => {
    dispatch(
      setFilters({
        organizationId: carriedOrganizationId,
        channel: CHANNEL_TYPE.CALL,
        minMessageCount: '',
        maxMessageCount: '',
        call_ids: undefined,
      })
    )
    const { startDate: defaultStart, endDate: defaultEnd } = callSearchInitialState.filters
    if (startDate === defaultStart && endDate === defaultEnd) {
      const now = new Date()
      dispatch(
        setDateRangeFilters({
          startDate: format(subDays(now, 59), "yyyy-MM-dd'T00:00:00'xxx"),
          endDate: format(now, "yyyy-MM-dd'T23:59:59'xxx"),
        })
      )
    }
  }, [])

  useEffect(() => {
    refreshCallCount()
  }, [refreshCallCount, organizationId])

  const handleApplyFilters = () => {
    if (analysisState !== ANALYSIS_STATES.idle) {
      resetAnalysis()
      setExpandedSections({})
    }
    refreshCallCount()
  }

  const handleClearFilters = () => {
    dispatch(clearAllFilters(organizationId))
    dispatch(setFilters({ organizationId }))
    dispatch(setDateRangeFilters({ startDate, endDate, dateRange: filters.dateRange }))
  }

  const organizationOptions = getOrganizationOptions(organizations)

  const hasValidDateRange =
    Boolean(startDate) && Boolean(endDate) && new Date(startDate) <= new Date(endDate)

  const hasEnoughCalls = isNil(totalCount) || totalCount >= MINIMUM_CALLS

  const isReadyToRun =
    Boolean(organizationId) && hasValidDateRange && Boolean(apiUrl) && !counting && hasEnoughCalls

  const canExport = analysisState === ANALYSIS_STATES.complete && !isEmpty(analysisReport?.sections)

  const selectedOrganizationName =
    organizationOptions.find((option) => option.value === Number(organizationId))?.label || ''

  const summaryCards = formatSummaryCards({
    report: analysisReport,
    selectedOrganization: organizationId,
    selectedPlaybooks: filters.playbooks,
    selectedAgents: filters.agents,
    startDate,
    endDate,
    organizationOptions,
  })

  const handleRunAnalysis = () => {
    startAnalysis({
      organization_id: Number(organizationId),
      start_date: startDate,
      end_date: endDate,
      call_filters: formatCallFilters(filters, callSearchData.playbookData),
    })
  }

  const handleExport = () => {
    if (isExporting || !canExport) {
      return
    }
    setIsExporting(true)
    try {
      exportToPDF({ report: analysisReport, summaryCards })
    } finally {
      setTimeout(() => setIsExporting(false), 1000)
    }
  }

  const handleExportClick = () => {
    if (isExporting || !canExport) {
      return
    }
    setShowExportWarning(true)
  }

  const handleConfirmExport = () => {
    setShowExportWarning(false)
    handleExport()
  }

  const handleToggleSection = (sectionType) => {
    setExpandedSections((prev) => ({ ...prev, [sectionType]: !prev[sectionType] }))
  }

  return (
    <div>
      <AutomationInsightsHeader />

      <AutomationInsightsFilters
        onRunAnalysis={handleRunAnalysis}
        onStopAnalysis={stopAnalysis}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        carriedOrganizationId={carriedOrganizationId}
        isReadyToRun={isReadyToRun}
        isRunning={analysisState === ANALYSIS_STATES.loading}
        counting={counting}
        totalCount={totalCount}
        hasKeywords={!isEmpty(filters.keywords)}
      />

      {analysisError && (
        <Banner critical header="Error" className="mb">
          {analysisError}
        </Banner>
      )}

      {analysisState === ANALYSIS_STATES.loading && (
        <AnalysisProgress progress={analysisProgress} message={analysisMessage} />
      )}

      {analysisState === ANALYSIS_STATES.complete && analysisReport && (
        <AutomationInsightsReport
          report={analysisReport}
          summaryCards={summaryCards}
          onExport={handleExportClick}
          isExporting={isExporting}
          canExport={canExport}
          expandedSections={expandedSections}
          onToggleSection={handleToggleSection}
        />
      )}

      {showExportWarning && (
        <BasicModal
          title="Before You Export"
          onClose={() => setShowExportWarning(false)}
          show={showExportWarning}
          size="tiny"
          data-testid="automation-insights-export-warning"
        >
          <p className="mb" data-testid="automation-insights-export-warning-text">
            Automation Insights reports are confidential and intended solely for use within
            {selectedOrganizationName ? ` ${selectedOrganizationName}` : ''}. They must not be
            shared with any other organization.
          </p>
          <div className="modal-footer align-right">
            <Button type="button" onClick={() => setShowExportWarning(false)}>
              Cancel
            </Button>
            <Button
              critical
              type="button"
              onClick={handleConfirmExport}
              data-testid="automation-insights-export-confirm"
            >
              I Understand, Export PDF
            </Button>
          </div>
        </BasicModal>
      )}
    </div>
  )
}

export default AutomationInsightsPage
